<h2 mat-dialog-title>{{dialogTitle}}</h2>

<mat-dialog-content class="dialog-content" [formGroup]="form">
  <div class="section">
    <h3>Scope</h3>
    <div class="row two-col">
      <mat-form-field appearance="outline">
        <mat-label>Brand</mat-label>
        <mat-select formControlName="vendorId" panelClass="admin-fee-select-search-panel">
          <mat-option class="admin-fee-search-option" [disabled]="true">
            <input
              matInput
              class="admin-fee-search-input"
              [formControl]="vendorSearchControl"
              placeholder="Search brands..."
              (keydown)="$event.stopPropagation()"
            >
          </mat-option>
          <mat-option *ngFor="let option of filteredVendorOptions" [value]="option.value">
            {{option.label}}
          </mat-option>
        </mat-select>
        <mat-error *ngIf="showVendorRequiredError()">Brand is required.</mat-error>
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Cart fee label (optional)</mat-label>
        <input matInput formControlName="cartLabel" placeholder="e.g. HVA fee">
      </mat-form-field>
    </div>

    <div class="row">
      <mat-form-field appearance="outline" class="full-width">
        <mat-label>More info HTML (optional)</mat-label>
        <textarea
          matInput
          formControlName="moreInfoHtml"
          rows="4"
          placeholder="e.g. Includes state filing and compliance support. <a href='https://example.com'>Learn more</a>"
        ></textarea>
        <mat-hint>Displayed to customers from an info bubble next to this fee in Shopping Cart.</mat-hint>
        <mat-error *ngIf="form.get('moreInfoHtml')?.hasError('maxlength')">
          Keep this under {{moreInfoHtmlMaxLength}} characters.
        </mat-error>
      </mat-form-field>
    </div>

    <div class="brand-config-panel" [class.warning]="canEditSelectedBrandConfig && !form.get('shoppingCart')?.value">
      <div class="brand-config-header">
        <div>
          <h4>Brand shopping cart config</h4>
          <div class="hint">{{selectedBrandConfigMessage}}</div>
        </div>
      </div>

      <div class="brand-config-values">
        <div>
          <span>content.ui.shoppingCart</span>
          <strong>{{getBrandConfigValueLabel('shoppingCart')}}</strong>
        </div>
        <div>
          <span>content.ui.hideEnrollButtonForShoppingCart</span>
          <strong>{{getBrandConfigValueLabel('hideEnrollButtonForShoppingCart')}}</strong>
        </div>
      </div>

      <div class="row two-col brand-config-toggles">
        <mat-slide-toggle formControlName="shoppingCart">
          Shopping cart enabled
        </mat-slide-toggle>
        <mat-slide-toggle formControlName="hideEnrollButtonForShoppingCart">
          Shopping-cart-only experience
        </mat-slide-toggle>
      </div>
    </div>

    <div class="row">
      <mat-slide-toggle
        formControlName="appliesToAllProducts"
        (change)="onAppliesToAllProductsChanged($event.checked)"
      >
        Applies to all products
      </mat-slide-toggle>
    </div>

    <div class="row two-col" *ngIf="form.get('appliesToAllProducts')?.value; else vendorProductScopeBlock">
      <mat-form-field appearance="outline">
        <mat-label>Charge style</mat-label>
        <mat-select formControlName="chargeMode" [disabled]="true">
          <mat-option value="line_item">Separate line item</mat-option>
        </mat-select>
      </mat-form-field>
      <div class="hint">All-product fees are charged as separate line items.</div>
    </div>

    <ng-template #vendorProductScopeBlock>
      <div class="row two-col">
        <mat-form-field appearance="outline">
          <mat-label>Vendor product</mat-label>
          <mat-select formControlName="vendorProductId" panelClass="admin-fee-select-search-panel">
            <mat-option class="admin-fee-search-option" [disabled]="true">
              <input
                matInput
                class="admin-fee-search-input"
                [formControl]="vendorProductSearchControl"
                placeholder="Search vendor products..."
                (keydown)="$event.stopPropagation()"
              >
            </mat-option>
            <mat-option *ngFor="let option of filteredVendorProductOptions" [value]="option.value">
              {{option.label}}
            </mat-option>
          </mat-select>
          <mat-error *ngIf="form.touched && !form.get('vendorProductId')?.value">Required when fee is product-specific.</mat-error>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Charge style</mat-label>
          <mat-select formControlName="chargeMode">
            <mat-option value="bundled">Include with product price</mat-option>
            <mat-option value="line_item">Separate line item</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="hint">Product-specific fees can include plan/state rules.</div>
    </ng-template>

    <div class="row two-col" *ngIf="requiresStripeProduct()">
      <mat-form-field appearance="outline">
        <mat-label>Stripe product ID</mat-label>
        <input matInput formControlName="stripeProductId" placeholder="prod_...">
        <mat-error *ngIf="form.touched && !form.get('stripeProductId')?.value">
          Required for separate line-item charging.
        </mat-error>
      </mat-form-field>
      <div class="hint">Used when fee is billed as a separate line item. Rule amount overrides Stripe price.</div>
    </div>
  </div>

  <div class="section">
    <h3>Amounts</h3>
    <div class="row two-col">
      <mat-form-field appearance="outline">
        <mat-label>Base amount (cents)</mat-label>
        <input matInput type="number" min="0" formControlName="amountCents">
      </mat-form-field>
      <div class="hint">Used when conditional rules are disabled.</div>
    </div>

    <div class="row">
      <mat-slide-toggle
        formControlName="useConditionalRules"
        (change)="onConditionalToggle($event.checked)"
      >
        Use conditional amount rules
      </mat-slide-toggle>
    </div>

    <div *ngIf="form.get('useConditionalRules')?.value" class="rules-section">
      <div class="row two-col">
        <mat-form-field appearance="outline">
          <mat-label>Default amount (cents)</mat-label>
          <input matInput type="number" min="0" formControlName="defaultAmountCents">
        </mat-form-field>
        <div class="hint">Applied when no specific rule matches.</div>
      </div>

      <div class="rules-header">
        <h4>Rules</h4>
        <button mat-stroked-button color="primary" type="button" (click)="addRule()">Add rule</button>
      </div>

      <div class="rule-card" *ngFor="let ruleControl of rules.controls; let i = index" [formGroup]="asFormGroup(ruleControl)">
        <div class="rule-title-row">
          <div class="rule-title">Rule #{{i + 1}}</div>
          <button mat-icon-button type="button" (click)="removeRule(i)" aria-label="Remove rule">
            <mat-icon>delete</mat-icon>
          </button>
        </div>

        <div class="row three-col">
          <mat-form-field appearance="outline">
            <mat-label>Amount (cents)</mat-label>
            <input matInput type="number" min="0" formControlName="amountCents">
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Plan condition</mat-label>
            <mat-select formControlName="planCondition">
              <mat-option value="ALL">All plans</mat-option>
              <mat-option value="ONLY">Only these plans</mat-option>
              <mat-option value="EXCEPT">All except these plans</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Plan levels</mat-label>
            <input
              matInput
              formControlName="planLevelsInput"
              placeholder="Premium, Easy Options"
            >
            <mat-hint>Comma-separated</mat-hint>
            <mat-error *ngIf="planLevelsRequired(ruleControl) && form.touched && !ruleControl.get('planLevelsInput')?.value">
              Required for selected plan condition.
            </mat-error>
          </mat-form-field>
        </div>

        <div class="row two-col">
          <mat-form-field appearance="outline">
            <mat-label>State condition</mat-label>
            <mat-select formControlName="stateCondition">
              <mat-option value="ALL">All states</mat-option>
              <mat-option value="ONLY">Only these states</mat-option>
              <mat-option value="EXCEPT">All except these states</mat-option>
            </mat-select>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>States</mat-label>
            <mat-select formControlName="states" multiple>
              <mat-option *ngFor="let state of stateOptions" [value]="state">
                {{state}}
              </mat-option>
            </mat-select>
            <mat-error *ngIf="statesRequired(ruleControl) && form.touched && !ruleControl.get('states')?.value?.length">
              Required for selected state condition.
            </mat-error>
          </mat-form-field>
        </div>

        <div class="rule-error" *ngIf="isRuleInvalid(ruleControl)">
          This rule is incomplete. Fill in required fields.
        </div>
      </div>

      <div class="rule-error" *ngIf="duplicateRuleError">{{duplicateRuleError}}</div>

      <div class="preview">
        <h4>Live preview</h4>
        <div class="row two-col">
          <mat-form-field appearance="outline">
            <mat-label>Preview plan level</mat-label>
            <input matInput formControlName="previewPlanLevel" [matAutocomplete]="planLevelAutocomplete">
            <mat-autocomplete #planLevelAutocomplete="matAutocomplete">
              <mat-option *ngFor="let plan of planLevelSuggestions" [value]="plan">{{plan}}</mat-option>
            </mat-autocomplete>
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Preview state</mat-label>
            <mat-select formControlName="previewState">
              <mat-option [value]="''">None</mat-option>
              <mat-option *ngFor="let state of stateOptions" [value]="state">{{state}}</mat-option>
            </mat-select>
          </mat-form-field>
        </div>

        <div class="preview-result" *ngIf="previewAmountCents !== null">
          <div>Resolved fee: <strong>{{previewAmountCents}}</strong> cents</div>
          <div>Matched: <strong>{{previewMatchedRuleSummary}}</strong></div>
        </div>
        <div class="hint" *ngIf="previewWarning">{{previewWarning}}</div>
      </div>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button mat-button type="button" (click)="cancel()">Cancel</button>
  <button mat-raised-button color="primary" type="button" [disabled]="!canSave()" (click)="save()">
    {{isEditMode ? 'Save' : 'Create'}}
  </button>
</mat-dialog-actions>
